import React, { useEffect, useState } from 'react';
import { BotIcon, MessageSquareIcon, CpuIcon, MessageSquarePlusIcon, PuzzleIcon, BookOpenIcon, CheckCircle2Icon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useAgentStore } from '../stores/agentStore';
import { useModelStore } from '../stores/modelStore';
import { useSkillStore } from '../stores/skillStore';
import { useKnowledgeStore } from '../stores/knowledgeStore';
import { useSessionStore, runtimeFromSession } from '../stores/sessionStore';
import { Agent } from '../types';
import { PageContainer, PageHeader, EmptyState } from '../components/common/page';
import { Card, Badge, Button, Select, Label } from '../components/common/ui';
import { Modal } from '../components/common/Modal';
import { AgentChatDialog } from '../components/chat/AgentChatDialog';
import { ChatDialog } from '../components/chat/ChatDialog';
export function ChatPage() {
  useScreenInit();
  const agents = useAgentStore(s => s.agents.filter(a => a.status === 'active'));
  const models = useModelStore(s => s.models);
  const sessions = useSessionStore(s => s.sessions);
  const [chatAgent, setChatAgent] = useState<Agent | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [tempSessionId, setTempSessionId] = useState<string | null>(null);
  const modelName = (id: string) => models.find(m => m.modelId === id)?.name || '未指定';
  const tempSession = sessions.find(s => s.sessionId === tempSessionId);
  return <PageContainer data-id="element-414">
      <PageHeader title="对话" description="新建临时会话，或选择一个 Agent 开始对话" data-id="element-415" />

      {/* Temporary chat entry */}
      <button onClick={() => setConfigOpen(true)} className="mb-6 flex w-full items-center gap-3 rounded-xl border border-dashed border-slate-700 bg-slate-900/40 px-5 py-4 text-left transition-colors hover:border-indigo-500/50 hover:bg-slate-800/40" data-id="element-416">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600/15" data-id="element-417">
          <MessageSquarePlusIcon className="h-5 w-5 text-indigo-400" data-id="element-418" />
        </div>
        <div data-id="element-419">
          <div className="text-sm font-medium text-slate-100" data-id="element-420">新建临时会话</div>
          <div className="text-xs text-slate-500" data-id="element-421">
            临时选配模型、Skills 与知识库，无需创建 Agent
          </div>
        </div>
      </button>

      {agents.length === 0 ? <EmptyState icon={MessageSquareIcon} title="暂无可用 Agent" description="请先在 Agent 中心创建并启用 Agent，或直接新建临时会话" data-id="element-422" /> : <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-423">
          {agents.map(a => <Card key={a.agentId} className="flex flex-col p-4" data-id="element-424">
              <div className="flex items-center gap-2.5" data-id="element-425">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600/15" data-id="element-426">
                  <BotIcon className="h-5 w-5 text-indigo-400" data-id="element-427" />
                </div>
                <div className="font-medium text-slate-100" data-id="element-428">{a.name}</div>
              </div>
              <p className="mt-2 line-clamp-2 flex-1 text-sm text-slate-400" data-id="element-429">
                {a.description}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-430">
                <Badge tone="indigo" data-id="element-431">
                  <CpuIcon className="h-3 w-3" data-id="element-432" /> {modelName(a.defaultModelId)}
                </Badge>
                {a.skills.map(s => <Badge key={s.skillId} tone="purple" data-id="element-433">
                    {s.name}
                  </Badge>)}
                {a.knowledgeBases?.map(kb => <Badge key={kb.knowledgeBaseId} tone="green" data-id="element-434">
                    {kb.name}
                  </Badge>)}
              </div>
              <Button size="sm" className="mt-4" onClick={() => setChatAgent(a)} data-id="element-435">
                <MessageSquareIcon className="h-3.5 w-3.5" data-id="element-436" /> 开始对话
              </Button>
            </Card>)}
        </div>}

      {chatAgent && <AgentChatDialog agent={chatAgent} open={!!chatAgent} onClose={() => setChatAgent(null)} data-id="element-437" />}

      <TemporaryConfigModal open={configOpen} onClose={() => setConfigOpen(false)} onConfirm={sessionId => {
      setConfigOpen(false);
      setTempSessionId(sessionId);
    }} data-id="element-438" />

      {tempSession && <ChatDialog runtime={runtimeFromSession(tempSession)} open={!!tempSessionId} onClose={() => setTempSessionId(null)} isTemporary existingSessionId={tempSession.sessionId} data-id="element-439" />}
    </PageContainer>;
}
function TemporaryConfigModal({
  open,
  onClose,
  onConfirm
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (sessionId: string) => void;
}) {
  const models = useModelStore(s => s.models.filter(m => m.enabled));
  const skills = useSkillStore(s => s.skills.filter(sk => sk.status === 'enabled'));
  const knowledgeBases = useKnowledgeStore(s => s.knowledgeBases.filter(kb => kb.status === 'ready'));
  const startTemporarySession = useSessionStore(s => s.startTemporarySession);
  const [modelId, setModelId] = useState('');
  const [skillIds, setSkillIds] = useState<string[]>([]);
  const [kbIds, setKbIds] = useState<string[]>([]);
  useEffect(() => {
    if (open) {
      setModelId(models[0]?.modelId || '');
      setSkillIds([]);
      setKbIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const toggle = (id: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  };
  const confirm = () => {
    if (!modelId) return;
    const sessionId = startTemporarySession({
      modelId,
      skillIds,
      knowledgeBaseIds: kbIds
    });
    onConfirm(sessionId);
  };
  return <Modal open={open} onClose={onClose} title="新建临时会话" description="选择模型并临时添加 Skills 与知识库" size="lg" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-441">
            取消
          </Button>
          <Button onClick={confirm} disabled={!modelId} data-id="element-442">
            开始对话
          </Button>
        </>} data-id="element-440">
      <div className="space-y-4" data-id="element-443">
        <div data-id="element-444">
          <Label required data-id="element-445">选择模型</Label>
          {models.length === 0 ? <p className="text-sm text-slate-500" data-id="element-446">
              暂无启用模型，请先在模型中心配置。
            </p> : <Select value={modelId} onChange={e => setModelId(e.target.value)} data-id="element-447">
              {models.map(m => <option key={m.modelId} value={m.modelId} data-id="element-448">
                  {m.name}
                </option>)}
            </Select>}
        </div>

        <div data-id="element-449">
          <Label data-id="element-450">临时添加 Skills</Label>
          {skills.length === 0 ? <p className="text-xs text-slate-500" data-id="element-451">暂无已启用的 Skill</p> : <div className="flex flex-wrap gap-2" data-id="element-452">
              {skills.map(s => {
            const active = skillIds.includes(s.skillId);
            return <button key={s.skillId} type="button" onClick={() => toggle(s.skillId, skillIds, setSkillIds)} className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors ${active ? 'border-purple-500/40 bg-purple-500/15 text-purple-200' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`} data-id="element-453">
                    <PuzzleIcon className="h-3 w-3" data-id="element-454" />
                    {s.name}
                    {active && <CheckCircle2Icon className="h-3 w-3" data-id="element-455" />}
                  </button>;
          })}
            </div>}
        </div>

        <div data-id="element-456">
          <Label data-id="element-457">临时添加 知识库</Label>
          {knowledgeBases.length === 0 ? <p className="text-xs text-slate-500" data-id="element-458">暂无 ready 状态的知识库</p> : <div className="flex flex-wrap gap-2" data-id="element-459">
              {knowledgeBases.map(kb => {
            const active = kbIds.includes(kb.knowledgeBaseId);
            return <button key={kb.knowledgeBaseId} type="button" onClick={() => toggle(kb.knowledgeBaseId, kbIds, setKbIds)} className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors ${active ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-200' : 'border-slate-700 text-slate-400 hover:bg-slate-800'}`} data-id="element-460">
                    <BookOpenIcon className="h-3 w-3" data-id="element-461" />
                    {kb.name}
                    {active && <CheckCircle2Icon className="h-3 w-3" data-id="element-462" />}
                  </button>;
          })}
            </div>}
        </div>
      </div>
    </Modal>;
}